// src/context/TaskContext.jsx

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
} from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  limit,
  startAfter,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { normalizeStatus } from "../hooks/useFirestore";

const TaskContext = createContext(null);

export function useTask() {
  const ctx = useContext(TaskContext);
  if (!ctx) throw new Error("useTask must be used inside <TaskProvider>");
  return ctx;
}

export function TaskProvider({ children }) {
  const [teamTasks, setTeamTasks] = useState([]);
  const [teamTasksLoading, setTeamTasksLoading] = useState(true);
  const [userTasks, setUserTasks] = useState([]);
  const [userTasksLoading, setUserTasksLoading] = useState(true);
const [unreadTaskCount, setUnreadTaskCount] = useState(0);


  const [allTasks, setAllTasks] = useState([]);
  const [allTasksLoading, setAllTasksLoading] = useState(true);
  const [allTasksLastDoc, setAllTasksLastDoc] = useState(null);
  const [allTasksHasMore, setAllTasksHasMore] = useState(true);

  const subscribeToAllTasks = useCallback(() => {
    setAllTasksLoading(true);
    const q = query(collection(db, "tasks"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      setAllTasks(snap.docs.map((d) => {
        const raw = d.data();
        return { id: d.id, ...raw, status: normalizeStatus(raw.status) };
      }));
      setAllTasksLoading(false);
    });

    return unsub;
  }, []);

  // ── Optimistic update functions ─────────────────────────
  const optimisticUpdateTaskStatus = useCallback(
    async (taskId, newStatus) => {
      const normalizedStatus = normalizeStatus(newStatus);
      // Find the task in any loaded list and update it optimistically
      const task =
        allTasks.find((t) => t.id === taskId) ||
        teamTasks.find((t) => t.id === taskId) ||
        userTasks.find((t) => t.id === taskId);
      if (!task) {
        throw new Error("Task not found");
      }

      // Optimistically update the task status
      setAllTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, status: normalizedStatus, updatedAt: serverTimestamp() }
            : t,
        ),
      );

      // Also update teamTasks if it exists there
      setTeamTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, status: normalizedStatus, updatedAt: serverTimestamp() }
            : t,
        ),
      );

      // Also update userTasks if it exists there
      setUserTasks((prev) =>
        prev.map((t) =>
          t.id === taskId
            ? { ...t, status: normalizedStatus, updatedAt: serverTimestamp() }
            : t,
        ),
      );

      try {
        const updates = {
          status: normalizedStatus,
          updatedAt: serverTimestamp(),
        };
        if (normalizedStatus === "COMPLETED") {
          updates.completedAt = serverTimestamp();
        }
        await updateDoc(doc(db, "tasks", taskId), updates);
      } catch (error) {
        // Rollback on error
        setAllTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
        setTeamTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
        setUserTasks((prev) => prev.map((t) => (t.id === taskId ? task : t)));
        throw error;
      }
    },
    [allTasks, teamTasks, userTasks],
  );

  const subscribeToTeamTasks = useCallback((leadId) => {
    if (!leadId) {
      setTeamTasks([]);
      setTeamTasksLoading(false);
      return () => {};
    }

    setTeamTasksLoading(true);
    const q = query(
      collection(db, "tasks"),
      where("teamId", "==", leadId),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setTeamTasks(snap.docs.map((d) => {
        const raw = d.data();
        return { id: d.id, ...raw, status: normalizeStatus(raw.status) };
      }));
      setTeamTasksLoading(false);
    });

    return unsub;
  }, []);

  const subscribeToUserTasks = useCallback((userId) => {
    if (!userId) {
      setUserTasks([]);
      setUserTasksLoading(false);
      return () => {};
    }

    setUserTasksLoading(true);
    const q = query(
      collection(db, "tasks"),
      where("assignedTo", "==", userId),
      orderBy("createdAt", "desc"),
    );

   const unsub = onSnapshot(q, (snap) => {
  const tasks = snap.docs.map((d) => {
    const raw = d.data();

    return {
      id: d.id,
      ...raw,
      status: normalizeStatus(raw.status),
    };
  });

  setUserTasks(tasks);

  setUnreadTaskCount(
    tasks.filter((task) => task.isRead === false).length
  );

  setUserTasksLoading(false);
});

    return unsub;
  }, []);

  const loadAllTasks = useCallback(
    async (reset = false) => {
      if (reset) {
        setAllTasks([]);
        setAllTasksLastDoc(null);
        setAllTasksHasMore(true);
      }
      setAllTasksLoading(true);
 
      let q = query(
        collection(db, "tasks"),
        orderBy("createdAt", "desc"),
        limit(20),
      );

      if (!reset && allTasksLastDoc) {
        q = query(q, startAfter(allTasksLastDoc));
      }

      try {
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => {
          const raw = d.data();
          return { id: d.id, ...raw, status: normalizeStatus(raw.status) };
        });
        setAllTasks((prev) => (reset ? data : [...prev, ...data]));
        setAllTasksLastDoc(snap.docs[snap.docs.length - 1] || null);
        setAllTasksHasMore(snap.docs.length === 20);
      } catch (error) {
        console.error("Error loading tasks:", error);
      } finally {
        setAllTasksLoading(false);
      }
    },
    [allTasksLastDoc],
  );

  const value = React.useMemo(
    () => ({
      teamTasks,
      teamTasksLoading,
      subscribeToTeamTasks,
      userTasks,
      userTasksLoading,
      subscribeToUserTasks,
      unreadTaskCount,
      
      allTasks,
      allTasksLoading,
      allTasksHasMore,
      loadAllTasks,
      subscribeToAllTasks,
      updateTaskStatus: optimisticUpdateTaskStatus,
    }),
    [
      teamTasks,
      teamTasksLoading,
      subscribeToTeamTasks,
      userTasks,
      userTasksLoading,
      subscribeToUserTasks,
      unreadTaskCount,

      allTasks,
      allTasksLoading,
      allTasksHasMore,
      loadAllTasks,
      subscribeToAllTasks,
      optimisticUpdateTaskStatus,
    ],
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}
